import { Divider, Icon, InlineText, Logo, SignInForm, Stack, Surface, Text, useT } from "@bundle/ui";
import type { ReactNode } from "react";
import { LegalAgreement } from "./LegalNotice.tsx";

export type SignInScreenProps = {
  /** Where to come back after an OAuth redirect (the current URL). */
  returnTo: string;
  mock: boolean;
  /** Prefilled dev-login email (mock mode). */
  defaultDevEmail?: string;
  /** Replaces the default heading (e.g. "Sign in to view this file"). */
  title?: string;
  /** Replaces the default line under the heading. */
  subtitle?: string;
  /** "New to Bundle? Create an account" (absent: no link). */
  onCreateAccount?: () => void;
};

/** Centered card used by the signed-out screens (sign in, create workspace, invite). */
export function AuthCard({ children, testID }: { children: ReactNode; testID: string }) {
  return (
    <Stack grow align="center" justify="center" padding="6" background="bg.window" testID={testID}>
      <Surface material="elevated" width="dialog" radius="xl" padding="8">
        <Stack gap="6">
          <Stack direction="horizontal" align="center">
            <Logo variant="lockup" size="avatar.md" />
          </Stack>
          {children}
        </Stack>
      </Surface>
    </Stack>
  );
}

/** Footer link between sign-in and Create account ("New to Bundle? Create an account" / "Already have an account? Sign in"). */
export function AuthSwitch({ question, action, onPress, testID }: { question: string; action: string; onPress: () => void; testID: string }) {
  return (
    <Stack gap="4" testID={testID}>
      <Divider />
      <Stack align="center">
        <InlineText runs={[{ text: `${question} ` }, { text: action, bold: true, onPress }]} variant="footnote" color="text.secondary" selectable={false} />
      </Stack>
    </Stack>
  );
}

/** Sign in: Google, email code, or (local development) dev login — whatever the server offers. */
export function SignInScreen({ returnTo, mock, defaultDevEmail, title, subtitle, onCreateAccount }: SignInScreenProps) {
  const t = useT();

  return (
    <AuthCard testID="sign-in-screen">
      <Stack gap="1">
        <Text variant="title2" weight="bold">
          {title ?? t("auth.title")}
        </Text>
        <Text color="text.secondary">{subtitle ?? t("auth.subtitle")}</Text>
      </Stack>
      <SignInForm returnTo={returnTo} defaultDevEmail={defaultDevEmail} />
      <LegalAgreement />
      {onCreateAccount === undefined ? null : <AuthSwitch question={t("auth.signup.newToBundle")} action={t("auth.signup.create")} onPress={onCreateAccount} testID="sign-in-to-signup" />}
      {mock ? (
        <Stack direction="horizontal" align="center" gap="2" justify="center">
          <Icon name="sparkles" size="icon.sm" color="text.tertiary" />
          <Text variant="caption" color="text.tertiary">
            {t("auth.mock")}
          </Text>
        </Stack>
      ) : null}
    </AuthCard>
  );
}
